import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../ts/Glob';
import * as permissions from '../../../_common/platform/ts/permissions';
import { ProgressBar } from '../../../_common/platform/tsx/ProgressBar';
import DirsTreeFlat, { calculateSelectAllState, DirsTreeModes, IContentItems } from './DirsTreeFlat';
import { config } from '../ts/config';

import DirsWorkspaceItem from './DirsWorkspaceItem';
import { IDraggableItemData } from '../../../_common/platform/ts/DragDropCommon';
import { IDirContentItem, IWorkspacesDirsInstanceState, getDirContentFromHash } from '../ts/stores/WorkspacesDirsStore';
import { MenuModes } from './menus/Common';
import { IMarkedItems } from '../ts/Interfaces';

function getStoreState(rootID: number, instanceID: string, glob: IGlob): IWorkspacesDirsInstanceState {
    return $.extend(
        true,
        {},
        glob.WorkspacesDirsStore.getInstanceState(instanceID, rootID || config.WIDGETS_ROOT_DIR_ID)
    );
}

export default function DirsWorkspacesFlat(props: {
    instanceID?: string;
    selectedDirId?: string | number;
    rootID?: number;
    rootName?: string;

    markMode?: boolean;
    markedDirs?: IMarkedItems;
    markedWorkspaces?: IMarkedItems;

    allowEditing?: boolean;
    useSearching?: boolean;
    disableMenu?: boolean;

    draggableDirData?: IDraggableItemData;
    draggableWorspaceData?: IDraggableItemData;
    hideIcons: boolean;
    menuMode: MenuModes;
    glob: IGlob;

    onWorkspaceSelect?(id: number, name: string): void;
    onDirSelect?(id: number, name: string): void;
    onShowActions?(): void;
    onHideActions?(): void;

    onWorkspacesMark?(marked: IMarkedItems): void;
    onDirsMark?(marked: IMarkedItems): void;
    onMark?(markedDirs: IMarkedItems, markedWorkspaces: IMarkedItems): void;
}) {
    const instanceID = props.instanceID || config.WIDGETS_ROOT_DIR_ID.toString();
    const [storeState, setStoreState] = React.useState<IWorkspacesDirsInstanceState>(
        getStoreState(props.rootID, instanceID, props.glob)
    );

    React.useEffect(() => {
        const handleOnStoreChange = () => {
            setStoreState(getStoreState(props.rootID, instanceID, props.glob));
        };

        props.glob.WorkspacesDirsStore.addChangeListener(handleOnStoreChange);
        props.glob.DirsStore.addChangeListener(handleOnStoreChange);

        const selectedDirID = props.selectedDirId;

        if (selectedDirID != null && selectedDirID !== storeState.displayDirId) {
            props.glob.AppActions.workspacesExplorerSetDisplayId(selectedDirID, instanceID);
            //result.displayDirId = selectedDirID;
        }

        props.glob.AppActions.workspacesExplorerSingleLoad(storeState.displayDirId, instanceID);

        return () => {
            props.glob.DirsStore.removeChangeListener(handleOnStoreChange);
            props.glob.WorkspacesDirsStore.removeChangeListener(handleOnStoreChange);
        };
    }, [props.rootID, instanceID, props.selectedDirId]);

    const handleOnContainerClick = React.useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDirSelect = (id: number, name: string) => {
        props.glob.AppActions.workspacesExplorerSetDisplayId(id, instanceID);
        props.glob.AppActions.workspacesExplorerSingleLoad(id, instanceID);

        if (typeof props.onDirSelect === 'function') {
            props.onDirSelect(id, name);
        }
    };

    // Select parent if self item deleted
    const handleItemDelete = (id: number) => {
        if (id === storeState.displayDirId) {
            const instanceState = props.glob.DirsStore.getInstanceState(instanceID);

            if (instanceState != null) {
                const dirObj = instanceState.data.dirsHash[id];
                const parentId = dirObj.parent.dirid;

                handleDirSelect(parentId, '');
            }
        }
    };

    const getWorkspaceSelectHandler = (name: string) => {
        return (id: number) => {
            if (typeof props.onWorkspaceSelect === 'function') {
                props.onWorkspaceSelect(id, name);
            }
        };
    };

    const getGetMarkHandler = (item: IDirContentItem) => {
        return (value: boolean) => {
            const result = $.extend(true, {}, props.markedWorkspaces);

            if (value) {
                result[item.wsid] = item.name;
            } else {
                delete result[item.wsid];
            }

            if (typeof props.onWorkspacesMark === 'function') {
                props.onWorkspacesMark(result);
            }
        };
    };

    const getWorkspaces = () => {
        const displaydFolderID = storeState.displayDirId;
        const dirContent = getDirContentFromHash(displaydFolderID, storeState.dirContentHash);

        if (dirContent == null || dirContent.length === 0) {
            return null;
        }

        let allowEditing = props.allowEditing;

        if (allowEditing !== false) {
            allowEditing = permissions.canEdit();
        }

        const content = _.map(dirContent, (item, i) => {
            const isFavorite = props.glob.WorkspacesDirsStore.isFavorite(item.wsid);

            return (
                <li key={i}>
                    <DirsWorkspaceItem
                        key={item.wsid}
                        instanceID={instanceID}
                        wsid={item.wsid}
                        name={item.name}
                        isFavourite={isFavorite}
                        displayDirId={displaydFolderID}
                        dragDropData={props.draggableWorspaceData}
                        allowEditing={allowEditing}
                        disableMenu={props.disableMenu}
                        changingWorkspaces={storeState.changingWorkspaces}
                        markMode={props.markMode}
                        marked={props.markMode && props.markedWorkspaces[item.wsid] !== undefined}
                        hideIcons={props.hideIcons}
                        menuMode={props.menuMode}
                        glob={props.glob}
                        onSelect={getWorkspaceSelectHandler(item.name)}
                        onShowActions={props.onShowActions}
                        onHideActions={props.onHideActions}
                        onMarkedChange={getGetMarkHandler(item)}
                    />
                </li>
            );
        });

        return <ul className="list-unstyled dirs-list-templates-flat">{content}</ul>;
    };

    const getContentKeys = (): IContentItems => {
        const dirs: IMarkedItems = {};
        const workspaces: IMarkedItems = {};
        const contentData = props.glob.WorkspacesDirsStore._state.data[instanceID];
        const contentHash = contentData && contentData.dirContentHash; //dirContentDict);
        const dirsData = props.glob.DirsStore._state.data[instanceID];
        const dirsHash = dirsData && dirsData.data && dirsData.data.dirsHash;

        if (dirsHash != null) {
            const { displayDirId } = storeState;
            const item = dirsHash[displayDirId as number];

            //VR: give the folders which are in the current shown folder:
            if (item != null) {
                _.each(item.subdirlist, (item) => {
                    if (item.dirid != null) {
                        dirs[item.dirid.toString()] = item.name;
                    }
                });
            }

            //VR: do the same for Workspaces: give those WIs which are in the current shown folder: (fixed: WI 106325)
            const contentItem = contentHash[displayDirId as number];

            if (contentItem != null) {
                _.each(contentItem, (item) => {
                    if (item.wsid != null) {
                        workspaces[item.wsid.toString()] = item.name;
                    }
                });
            }
        }

        return {
            dirs: dirs,
            items: workspaces
        };
    };

    const getSelectAllState = () => {
        const content = getContentKeys();

        return calculateSelectAllState(content, props.markedDirs, props.markedWorkspaces);
    };

    const handleOnSelectAllChange = (value: boolean) => {
        if (typeof props.onMark === 'function') {
            if (value) {
                const content = getContentKeys();

                props.onMark(content.dirs, content.items);
            } else {
                props.onMark({}, {});
            }
        }
    };

    const getContentLengthHandler = () => {
        return (dirID: number) => {
            const result = $.Deferred<number>();

            props.glob.WorkspacesDirsStore.loadSingleDirContent(dirID, instanceID).done((resp) => {
                result.resolve(resp.result.length);
            });

            return result;
        };
    };

    const rootID = props.rootID != null ? props.rootID : config.WIDGETS_ROOT_DIR_ID;
    const rootName = props.rootName != null ? props.rootName : config.WIDGETS_ROOT_DIR_NAME;
    const { loading } = storeState;

    let workspaces: React.JSX.Element;

    if (loading) {
        workspaces = <ProgressBar />;
    } else {
        workspaces = getWorkspaces();
    }

    let allowEditing = props.allowEditing;

    if (allowEditing !== false) {
        allowEditing = permissions.canEdit();
    }

    return (
        <div
            className={'dirs-flat dirs-workspaces-flat' + (loading ? ' loading' : '')}
            onClick={handleOnContainerClick}
        >
            <DirsTreeFlat
                instanceId={instanceID}
                mode={DirsTreeModes.WORKSPACE_SELECT}
                rootId={rootID}
                rootName={rootName}
                displayId={storeState.displayDirId as number}
                useSearching={props.useSearching}
                dragDropData={props.draggableDirData}
                canEdit={allowEditing}
                disableMenu={props.disableMenu}
                showSelectAll={props.markMode}
                markMode={props.markMode}
                marked={props.markedDirs}
                hideIcons={props.hideIcons}
                glob={props.glob}
                getContentLength={getContentLengthHandler()}
                getSelectAllState={getSelectAllState}
                onMark={props.onDirsMark}
                onSelect={handleDirSelect}
                onItemDelete={handleItemDelete}
                onShowActions={props.onShowActions}
                onHideActions={props.onHideActions}
                onSelectAllChange={handleOnSelectAllChange}
            />

            {workspaces}
        </div>
    );
}
